/**
 * Соответствие кода примитива геометрии (KLS / geometry_primitive_code) и инструментов MapLibre Draw.
 * Единая точка для home / ObjectPanel при выборе инструмента редактирования.
 */

export const PRIMITIVE_CODE_TO_DRAW_TOOL: Record<string, string> = {
  point: "draw-point",
  marker: "draw-point",
  multipoint: "draw-point",
  linestring: "draw-line",
  line: "draw-line",
  polyline: "draw-line",
  multilinestring: "draw-line",
  polygon: "draw-polygon",
  multipolygon: "draw-polygon",
  rectangle: "draw-rectangle",
  circle: "draw-circle",
};

/** Допустимые инструменты для примитива (первый — по умолчанию при несовпадении запроса). */
export const DRAW_TOOLS_BY_PRIMITIVE: Record<string, string[]> = {
  point: ["draw-point"],
  multipoint: ["draw-point"],
  line: ["draw-line", "draw-freehand"],
  linestring: ["draw-line", "draw-freehand"],
  polyline: ["draw-line", "draw-freehand"],
  multilinestring: ["draw-line", "draw-freehand"],
  polygon: ["draw-polygon", "draw-freehand", "draw-rectangle", "draw-circle", "draw-angled-rectangle", "draw-sensor", "draw-sector"],
  multipolygon: ["draw-polygon", "draw-freehand", "draw-rectangle", "draw-circle", "draw-angled-rectangle", "draw-sensor", "draw-sector"],
  rectangle: ["draw-rectangle"],
  circle: ["draw-circle"],
};

/** Полный список инструментов рисования (единый источник для тулбара и диалогов). */
export const DRAW_TOOLS: string[] = [
  "draw-point",
  "draw-line",
  "draw-polygon",
  "draw-rectangle",
  "draw-circle",
  "draw-freehand",
  "draw-angled-rectangle",
  "draw-sensor",
  "draw-sector",
];

export function normalizePrimitiveCode(raw: string): string {
  return String(raw || "")
    .trim()
    .toLowerCase();
}

/** Режим одной сессии рисования с несколькими контурами/линиями/точками. */
export type MultiPrimitiveSessionKind = "multipoint" | "multilinestring" | "multipolygon";

/**
 * Если выбран Multi*-примитив в KLS — включаем многошаговое рисование на карте.
 * Иначе null (одиночный Point/LineString/Polygon как раньше).
 */
export function primitiveCodeToMultiMode(
  code: string | null | undefined,
): MultiPrimitiveSessionKind | null {
  const n = normalizePrimitiveCode(code || "");
  if (n === "multipoint") return "multipoint";
  if (n === "multilinestring") return "multilinestring";
  if (n === "multipolygon") return "multipolygon";
  return null;
}

/**
 * Выбор инструмента рисования по коду примитива и опционально запрошенному инструменту.
 */
export function resolveDrawToolForPrimitive(
  primitiveCode: string,
  requestedTool: string | null | undefined
): string {
  const normalized = normalizePrimitiveCode(primitiveCode);
  const allowed = DRAW_TOOLS_BY_PRIMITIVE[normalized] ?? ["draw-polygon"];
  if (requestedTool && allowed.includes(requestedTool)) return requestedTool;
  return allowed[0];
}

/** Для режима редактирования существующей геометрии (как в home handleEditGeometry). */
export function mapPrimitiveCodeToDrawTool(
  geometryPrimitiveCode: string | undefined,
  geometryType: string | undefined
): string {
  const fromCode = geometryPrimitiveCode
    ? normalizePrimitiveCode(geometryPrimitiveCode)
    : "";
  if (fromCode && PRIMITIVE_CODE_TO_DRAW_TOOL[fromCode]) {
    return PRIMITIVE_CODE_TO_DRAW_TOOL[fromCode];
  }
  const fromType = normalizePrimitiveCode(geometryType || "");
  if (fromType && PRIMITIVE_CODE_TO_DRAW_TOOL[fromType]) {
    return PRIMITIVE_CODE_TO_DRAW_TOOL[fromType];
  }
  return "draw-polygon";
}
